import { SEND_NOTIFICATION, GET_USER_GROUPS } from "../api-calls/api-urls";
import {
  api,
  authApi,
  getAuthorizationHeader,
} from "../api-calls/user-requests";
import {
  SaveGroupsList,
  SaveMessages,
  PrependMessages,
  MergeLatestMessages,
  PatchMessage,
} from "../Redux/actions/chatActions";
import mqtt from "mqtt";
import store from "../Redux/store";

const MqttBrokerUrl = async (force = false) => {
  const endpoint = force
    ? `/map/get-aws-token?force=true`
    : `/map/get-aws-token`;
  const { data } = await authApi.get(endpoint, {
    headers: { Authorization: getAuthorizationHeader() },
  });
  let url = data?.signedUrl;
  if (!url) {
    console.error("[MqttBrokerUrl] No signedUrl in response:", data);
    throw new Error("MQTT signed URL is missing from backend response");
  }
  url = url.trim();
  return url;
};

let client = null;
let isConnecting = false;
let intentionalDisconnect = false;
let lastConnectParams = null;
let listRefreshDebounceTimer = null;
const persistentSubscriptions = new Set();

// Marks a conversation seen server-side. Used when a chat event arrives over
// MQTT for the conversation the user currently has open, so the sender's
// read receipt updates without the receiver having to leave and re-enter
// the chat screen.
const markConversationSeen = async (conversationId) => {
  try {
    await authApi.put(
      `/messages/mark-seen/${conversationId}`,
      {},
      { headers: { Authorization: getAuthorizationHeader() } },
    );
  } catch (err) {
    console.log("[markConversationSeen] failed:", err);
  }
};
// Stores the most recent map Livedata request so it can be flushed as soon as
// MQTT connects (handles the race condition where getMqttData("map") is called
// while the broker connection is still being established).
let pendingMapRequest = null;

export const ConnectMqtt = () => {};

export const publishFn = (topic, message) => {
  if (client && client.connected) {
    client.publish(topic, JSON.stringify(message), { qos: 0, retain: false });
  } else {
    console.warn("[publishFn] MQTT not connected, message dropped:", topic);
  }
};

export const subscribeToChatTopic = (topic) => {
  if (client && client.connected) {
    client.subscribe(topic);
  }
};

/**
 * Register a topic that must stay subscribed across MQTT reconnects.
 * Subscribes immediately if already connected, and re-subscribes automatically
 * every time the MQTT client reconnects.
 */
export const addPersistentSubscription = (topic) => {
  persistentSubscriptions.add(topic);
  if (client && client.connected) {
    client.subscribe(topic);
  }
};

const connectMqtt = async (id, userList, dispatch, force = false) => {
  if (client?.connected || isConnecting) {
    return;
  }
  isConnecting = true;

  const clientId = `teamplayer-${Math.random().toString(16).substr(2, 8)}`;

  lastConnectParams = { id, userList, dispatch };
  intentionalDisconnect = false;

  dispatch({ type: "MQTT_STATUS", payload: "connecting" });

  let MqttURL;
  try {
    MqttURL = await MqttBrokerUrl(force);
  } catch (err) {
    console.error("[connectMqtt] failed to fetch broker URL:", err);
    isConnecting = false;
    dispatch({ type: "MQTT_STATUS", payload: "error" });
    return;
  }

  if (client?.connected) {
    isConnecting = false;
    return;
  }

  // mqtt.js v5 internally parses the URL with `new URL()`, extracting only the pathname
  // (/mqtt) and discarding the query string (?X-Amz-Algorithm=...). When it reconstructs
  // the URL for `new WebSocket(url)`, the SigV4 query params are gone → AWS returns 403.
  // Fix: `transformWsUrl` is called just before `new WebSocket()`. We capture the original
  // full signed URL in the closure and always return it, bypassing mqtt.js URL reconstruction.
  const signedUrlForWs = MqttURL;
  client = mqtt.connect(MqttURL, {
    clientId: clientId,
    clean: true,
    reconnectPeriod: 0,
    protocolVersion: 4,
    keepalive: 300,
    connectTimeout: 30000,
    transformWsUrl: () => {
      return signedUrlForWs;
    },
  });

  client.on("connect", () => {
    isConnecting = false;
    dispatch({ type: "MQTT_STATUS", payload: "connected" });
    subscribeTopics(id, userList);

    if (pendingMapRequest) {
      const { topic: mapTopic, userList: mapUserList } = pendingMapRequest;
      pendingMapRequest = null;
      client.subscribe(mapTopic, (err) => {
        if (err)
          console.error(
            "[connectMqtt] failed to subscribe to map topic:",
            mapTopic,
            err,
          );
      });
      mapUserList.forEach((player) => {
        const companyId =
          player?.companies_registered?.company ||
          player?.department?.company_id;
        if (!companyId) {
          console.warn(
            "[connectMqtt] pendingMapRequest: skipping player with no companyId:",
            player?.user_id?._id,
          );
          return;
        }
        client.publish(
          `App/${companyId}/${player.user_id._id}/Livedata`,
          JSON.stringify({ topic: mapTopic }),
          { qos: 0, retain: false },
        );
      });
    }
  });

  client.on("close", () => {
    console.warn("[connectMqtt] connection CLOSED");
    client = null;
    isConnecting = false;
    dispatch({ type: "MQTT_STATUS", payload: "disconnected" });

    // Reconnect with force=true so the backend is guaranteed to issue brand-new
    // Cognito credentials. This is the key fix: if the previous connection was
    // rejected because the Cognito session token had expired, retrying with the
    // same cached URL would fail again. Forcing a cache bypass on every reconnect
    // ensures we never get stuck in a credentials-expired retry loop.
    if (!intentionalDisconnect && lastConnectParams) {
      const {
        id: lastId,
        userList: lastUserList,
        dispatch: lastDispatch,
      } = lastConnectParams;
      setTimeout(() => {
        if (!intentionalDisconnect && !isMqttConnected()) {
          connectMqtt(lastId, lastUserList, lastDispatch, true);
        }
      }, 5000);
    }
  });

  client.on("offline", () => {
    console.warn("[connectMqtt] client OFFLINE");
    dispatch({ type: "MQTT_STATUS", payload: "disconnected" });
  });

  client.on("message", (topic, message) => {
    try {
      const regex = /^App\/[a-z0-9]+\/[a-z0-9]+\/Status$/;
      const regexMapData = /^App\/[a-z0-9]+$/;
      const chatTopic = "App/Chat";

      if (topic.startsWith(chatTopic)) {
        const raw = message.toString();
        if (!raw) return;

        let payload = null;
        try {
          payload = JSON.parse(raw);
        } catch (e) {
          // Not JSON — fall through with payload = null, still refresh the
          // conversation list below.
        }

        // Refresh the conversation list (unread badges, last-message
        // preview) on every chat event. Debounced so a burst of messages —
        // e.g. someone catching up on several unread group chats at once —
        // doesn't fire a burst of list refetches.
        if (listRefreshDebounceTimer) clearTimeout(listRefreshDebounceTimer);
        listRefreshDebounceTimer = setTimeout(() => {
          dispatch(getUserGroupList());
        }, 300);

        const userInfoRaw = sessionStorage.getItem("userInfo");
        const selectedChatRaw = sessionStorage.getItem("selectedChat");
        const companyData = userInfoRaw ? JSON.parse(userInfoRaw) : null;
        const selectedChatData = selectedChatRaw
          ? JSON.parse(selectedChatRaw)
          : null;

        // Only refetch messages when this event actually belongs to the
        // conversation currently open — the old handler refetched the open
        // conversation for every chat event on every topic this client is
        // subscribed to, regardless of which conversation it was for.
        const isOpenConversation =
          payload?.conversation_id &&
          selectedChatData?._id &&
          payload.conversation_id === selectedChatData._id;

        if (isOpenConversation && companyData?._id) {
          // Edits and deletes carry the change, so apply them in place — the
          // message may be in an older page the latest-page refresh won't
          // fetch. Everything else (new message, read ticks) refreshes the
          // newest page. No artificial delay needed — the backend awaits the
          // save before it publishes.
          if (payload?.type === "messageEdited" && payload.message_id) {
            dispatch(
              PatchMessage(selectedChatData._id, payload.message_id, {
                message: payload.message,
                is_edited: true,
                edited_at: payload.edited_at,
              }),
            );
          } else if (payload?.type === "messageDeleted" && payload.message_id) {
            dispatch(
              PatchMessage(selectedChatData._id, payload.message_id, {
                is_deleted: true,
                message: "",
                file_url: "",
                file_name: "",
              }),
            );
          } else {
            dispatch(refreshLatestConversationMessages(selectedChatData));
          }

          // A message arrived (or was edited) in the conversation the user
          // is actively looking at — mark it seen right away instead of
          // waiting for them to leave and re-enter the chat.
          if (payload?.type !== "messagesSeen") {
            markConversationSeen(selectedChatData._id);
          }
        }
        return;
      }

      if (regex.test(topic)) {
        changeStatus(JSON.parse(message.toString()), dispatch);
      } else if (regexMapData.test(topic)) {
        changePosition(JSON.parse(message.toString()), dispatch);
      }
    } catch (err) {
      console.log(err, message.toString());
    }
  });

  client.on("error", (err) => {
    isConnecting = false;
    console.error("MQTT Client Error:", err);
    dispatch({ type: "MQTT_STATUS", payload: "error" });
  });
};

export const disconnectMqtt = (dispatch) => {
  intentionalDisconnect = true;
  lastConnectParams = null;
  if (client) {
    client.end(true);
    client = null;
    isConnecting = false;
  }
  // Clear persistent subscriptions too — otherwise the next user to connect in
  // this tab (e.g. a different account on a shared browser) replays the
  // previous user's private topics, including App/Chat/Message/{userId}.
  persistentSubscriptions.clear();
  pendingMapRequest = null;
  dispatch({ type: "MQTT_STATUS", payload: "disconnected" });
};

const subscribeTopics = (id, userList) => {
  try {
    const chatTopic = `App/Chat/${id}`;
    client.subscribe(chatTopic, (err) => {
      if (err) console.error("[subscribeTopics] failed:", chatTopic, err);
    });
    userList.forEach(async (playerId) => {
      client.subscribe(`App/${id}/${playerId.user_id._id}/Status`);
    });
    persistentSubscriptions.forEach((topic) => {
      client.subscribe(topic, (err) => {
        if (err)
          console.error("[subscribeTopics] failed persistent:", topic, err);
      });
    });
  } catch (err) {
    console.log(err);
  }
};

const isMqttConnected = () => {
  return client !== null && client.connected;
};

export const getMqttData = async (id, userList, dispatch, from) => {
  let topic = null;
  if (from === "map") {
    const { data } = await api.post(`${SEND_NOTIFICATION}/${id}`);
    topic = data.topic;
    localStorage.setItem("mqttTopic", data.topic);
  }

  if (!isMqttConnected() && !isConnecting) {
    connectMqtt(id, userList, dispatch);
  }

  if (topic === null) return;

  pendingMapRequest = { topic, userList };

  if (client?.connected) {
    pendingMapRequest = null;
    client.subscribe(topic);
    userList.forEach((playerId) => {
      if (from === "map") {
        const companyId =
          playerId?.companies_registered?.company ||
          playerId?.department?.company_id;
        if (!companyId) {
          console.warn(
            "[getMqttData] skipping player with no companyId:",
            playerId?.user_id?._id,
          );
          return;
        }
        client.publish(
          `App/${companyId}/${playerId.user_id._id}/Livedata`,
          JSON.stringify({ topic: `${topic}` }),
          { qos: 0, retain: false },
        );
      }
    });
  }
};

const changePosition = async (data, dispatch) => {
  try {
    const { teamMembersList } = store.getState()?.allUsers;
    const updatedTeamMembersList = teamMembersList.map((Player) => {
      if (Player?.user_id?._id === data?.user_id) {
        return {
          ...Player,
          position: {
            lat: parseFloat(
              data?.lat || data?.location?.lat || data?.location_info?.lat,
            ),
            lng: parseFloat(
              data?.lng || data?.location?.lng || data?.location_info?.lng,
            ),
          },
          status: data?.status || "checkout",
          batteryLevel: data?.batteryLevel,
        };
      }
      return Player;
    });
    dispatch({ type: "TEAM_MEMBERS_LIST", payload: updatedTeamMembersList });
  } catch (err) {
    console.log(err);
  }
};

const changeStatus = async (data, dispatch) => {
  const { teamMembersList } = store.getState()?.allUsers;

  let topic = localStorage.getItem("mqttTopic");
  const companyid = data.company_id;
  const userid = data.user_id;

  client.publish(
    `App/${companyid}/${userid}/Livedata`,
    JSON.stringify({ topic: `${topic}` }),
    { qos: 0, retain: false },
  );

  const updatedTeamMembersList = teamMembersList.map((player) => {
    if (player?.user_id?._id === data?.user_id) {
      const positionFromData = data?.location || data?.location_info;
      return {
        ...player,
        status: data?.status,
        position: positionFromData
          ? {
              lat: parseFloat(positionFromData.lat),
              lng: parseFloat(positionFromData.lng),
            }
          : player.position,
        batteryLevel: data?.batteryLevel,
        ...(data?.device_id && { device_id: data?.device_id }),
      };
    }
    return player;
  });
  dispatch({ type: "TEAM_MEMBERS_LIST", payload: updatedTeamMembersList });
};

export const findOnline = async (id) => {
  try {
    const { data } = await authApi.get(`users/time-keeper/${id}`, {
      headers: { Authorization: getAuthorizationHeader() },
    });
    const currentTime = new Date();
    const timestamp = currentTime.getTime();
    const targetTime = new Date(data.time);
    const targetTimestamp = targetTime.getTime();
    const difference = timestamp - targetTimestamp;
    if (difference / 1000 < 30) {
      return "online";
    } else {
      return "offline";
    }
  } catch (error) {
    return "offline";
  }
};

export const PublishMessage = (topic) => {
  if (client && client.connected) {
    client.publish(topic, JSON.stringify({ forcelogout: 1 }), {
      qos: 0,
      retain: true,
    });
  } else {
    console.warn(
      "[PublishMessage] MQTT not connected, message dropped:",
      topic,
    );
  }
};

export const getUserGroupList = () => async (dispatch) => {
  try {
    const { data } = await authApi.get(`${GET_USER_GROUPS}`, {
      headers: { Authorization: getAuthorizationHeader() },
    });
    dispatch(SaveGroupsList(data));
  } catch (error) {
    console.log(error);
  }
};

// `company` is accepted for call-site backward compatibility but no longer
// used in the request — the backend controller never read the :companyid
// route segment (chatController.js's getConvoMessages only uses
// req.params.id), so this now calls the plain /messages/conversations/:id
// route instead.
export const getConversationMessages =
  (member, _company) => async (dispatch) => {
    try {
      const { data } = await authApi.get(
        `/messages/conversations/${member?._id}`,
        {
          headers: { Authorization: getAuthorizationHeader() },
        },
      );
      dispatch(SaveMessages({ ...data, conversation_id: member?._id }));
    } catch (error) {
      console.log(error);
    }
  };

// Refetches only the newest page of the open conversation and merges it in,
// for new messages / read ticks. Replacing the list with page 1 (what
// getConversationMessages does) threw away every older page the user had
// scrolled up to.
export const refreshLatestConversationMessages = (member) => async (dispatch) => {
  try {
    const { data } = await authApi.get(`/messages/conversations/${member?._id}`, {
      headers: { Authorization: getAuthorizationHeader() },
    });
    dispatch(MergeLatestMessages({ ...data, conversation_id: member?._id }));
  } catch (error) {
    console.log(error);
  }
};

// Fetches the page of history older than `before` (the _id of the oldest
// loaded message) and prepends it to the currently loaded messages. `page`
// is sent too for backends that predate the cursor. Returns the raw
// response (including `pagination`) so the caller can tell whether another
// older page remains, and can restore scroll position once the DOM updates.
export const loadOlderConversationMessages =
  (member, company, page, before) => async (dispatch) => {
    try {
      const cursor = before ? `&before=${encodeURIComponent(before)}` : "";
      const { data } = await authApi.get(
        `/messages/conversations/${member?._id}?page=${page}${cursor}`,
        {
          headers: { Authorization: getAuthorizationHeader() },
        },
      );
      dispatch(PrependMessages({ ...data, conversation_id: member?._id }));
      return data;
    } catch (error) {
      console.log(error);
      return null;
    }
  };
